import React, { useState } from "react";
import * as Mui from "@mui/material";
import * as Icons from "react-feather";
import { useSelector } from "react-redux";
import classNames from "classnames";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import { CategoryAccordion } from "component/shared/category-accordion";
import { RouterLinkWrapper } from "component/shared/router-link-wrapper";
import { Column } from "component/shared/table";
import { RefreshTable } from "component/shared/refresh-table";
import { Tag } from "component/shared/tag";
import { RootState } from "redux/store";
import { profileFromFeaturedMlsIds } from "routes/profile-navigation";
import { AgentMlsIds, MlsIdBoard } from "model/agent-mls-ids";
import { DisplayRow, featuredDisplayRows, problemsFirst, RowAction } from "./rows";
import { BoardCell } from "../board-cell";
import { OfficeIdHeader } from "../office-id-header";
import { PendingTag } from "../pending-tag";
import { styles } from "./style";

export type FeaturedView = "table" | "board";

interface Props extends WithStyles<typeof styles> {
	agentMlsIds: AgentMlsIds;
	view: FeaturedView;
	// Only the rows on Featured Listings.
	featuredOnly: boolean;
	onAction: (action: RowAction) => void;
}

const Component: React.FC<Props> = ({ agentMlsIds, view, featuredOnly, onAction, classes = {} }) => {
	// The board sections the user opened or closed; the rest keep their starting state.
	const [open, setOpen] = useState<Record<number, boolean>>({});
	const userId = useSelector((state: RootState) => state.authentication.user?.agentId);
	const boards = agentMlsIds.boards;

	const columns = (withBoard: boolean): Column<DisplayRow>[] => [
		{
			id: "board",
			title: "MLS Board",
			hidden: !withBoard,
			value: (row) => <BoardCell board={boards.find((b) => b.boardId === row.boardId)} />,
		},
		{
			id: "agent",
			title: "Agent",
			value: (row) => (row.unknown
				? <span className={classes.muted}>Unknown</span>
				: (
					<span>
						{row.agents.map((agent, i) => (
							<React.Fragment key={agent.id}>
								{i > 0 && ", "}
								<Mui.Link component={RouterLinkWrapper} to={profileFromFeaturedMlsIds(agent.id)}>{agent.name}</Mui.Link>
							</React.Fragment>
						))}
					</span>
				)),
		},
		{
			id: "mlsId",
			title: "Agent MLS ID",
			value: (row) => (
				<div className={classes.stack}>
					<span className={classNames(row.mlsId === null && classes.muted)}>{row.mlsId ?? "None"}</span>
					{row.issue && (
						<Mui.Tooltip title={row.issue.tip} placement="bottom">
							<span className={classes.issue} tabIndex={0}>
								<Tag label={row.issue.label} variant="warning" size="small" icon={<Icons.AlertTriangle size={12} />} />
							</span>
						</Mui.Tooltip>
					)}
				</div>
			),
		},
		{
			id: "alternateId",
			title: <OfficeIdHeader />,
			value: (row) => (
				<span className={classNames((row.alternateId === null || row.featuredInfo !== null) && classes.muted)}>{row.alternateId ?? "None"}</span>
			),
		},
		{
			id: "onFeatured",
			title: "On Featured Listings",
			value: (row) => (
				<span className={classes.featured}>
					{row.onFeatured && <span role="img" aria-label="Yes" className={classes.featured}><Icons.Check size={20} /></span>}
					{row.featuredInfo && (
						<Mui.Tooltip title={row.featuredInfo}>
							<span className={classes.info} tabIndex={0} aria-label={row.featuredInfo}>
								<Icons.Info size={16} />
							</span>
						</Mui.Tooltip>
					)}
				</span>
			),
		},
		{
			id: "action",
			title: "Action",
			align: "right",
			value: (row) => (
				<Mui.IconButton aria-label={row.label} title={row.label} onClick={() => onAction(row.action)}>
					{row.label === "Review" ? <Icons.Tool size={20} /> : <Icons.Edit size={20} />}
				</Mui.IconButton>
			),
		},
	];

	const table = (rows: DisplayRow[], withBoard: boolean, paginate: boolean, embedded = false) => (
		<RefreshTable
			items={rows}
			columns={columns(withBoard)}
			// The rows come in the page's order; no column sorts them.
			sort={{ id: "", desc: false }}
			rowKey={(row) => row.key}
			paginate={paginate}
			embedded={embedded}
			userId={userId}
			tableKey="featured-mls-ids"
		/>
	);

	const rowsOf = (board: MlsIdBoard) => featuredDisplayRows(agentMlsIds, board.boardId).filter((row) => !featuredOnly || row.onFeatured);
	// The table puts every board's rows that need review first.
	const allRows = problemsFirst(boards.flatMap(rowsOf));
	if (allRows.length === 0) {
		return (
			<Mui.Typography className={classes.empty}>
				{featuredOnly ? "No MLS IDs are on Featured Listings yet." : "No MLS IDs added yet."}
			</Mui.Typography>
		);
	}
	if (view === "board") {
		const shown = boards.map((board) => ({ board, rows: rowsOf(board) })).filter(({ rows }) => rows.length > 0);
		// A few boards start open; with more, only the ones with issues do.
		const startsOpen = (rows: DisplayRow[]) => shown.length <= 3 || rows.some((row) => row.issue);
		return (
			<>
				{shown.map(({ board, rows }) => {
					const meta = boardMeta(rows);
					const expanded = open[board.boardId] ?? startsOpen(rows);
					return (
						<CategoryAccordion
							key={board.boardId}
							className={classes.board}
							summaryClassName={classes.boardSummary}
							expanded={expanded}
							onToggle={() => setOpen({ ...open, [board.boardId]: !expanded })}
							disableDetailsPadding
							summary={
								<div className={classes.boardHeader}>
									<span className={classes.boardTitle}>
										{expanded ? <Icons.ChevronUp size={24} className={classes.chevron} /> : <Icons.ChevronDown size={24} className={classes.chevron} />}
										{board.name}
										{board.pending && <PendingTag />}
									</span>
									<span className={classes.boardMeta}>
										{meta.issues && (
											<>
												<Icons.AlertTriangle size={16} className={classes.boardWarningIcon} aria-hidden />
												{meta.issues}
												<span aria-hidden="true">{" • "}</span>
											</>
										)}
										{meta.onFeatured}
									</span>
								</div>
							}
						>
							<div className={classes.boardBody}>
								{table(rows, false, false, true)}
							</div>
						</CategoryAccordion>
					);
				})}
			</>
		);
	}
	return table(allRows, true, true);
};

export const FeaturedAgentTable = withStyles(Component, styles);

// A board section's header: "M issues • N on Featured Listings", leaving out the issues when there are none.
const boardMeta = (rows: DisplayRow[]) => {
	const issueCount = rows.filter((row) => row.issue).length;
	const issues = issueCount ? `${issueCount} ${issueCount === 1 ? "issue" : "issues"}` : null;
	const onFeatured = `${rows.filter((row) => row.onFeatured).length} on Featured Listings`;
	return { issues, onFeatured, text: issues ? `${issues} • ${onFeatured}` : onFeatured };
};
